"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { Calendar, ChevronDown, SlidersHorizontal } from "lucide-react";
import { addDays, format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import i18n from "i18next";
import { useDebouncedCallback } from "use-debounce";

import CreatePaymentDialog from "../components/CreatePaymentDialog/CreatePaymentDialog.tsx";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Calendar as UICalendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import GenericLoader from "@/components/GenericLoader";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { currentLanguageLocale, getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import useUserStore from "@/features/auth/store/useUserStore";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import PATHS from "@/routes/paths";
import EditPaymentDialog from "@/features/finances/components/EditPaymentDialog";

type PaymentStatusCode =
  | "paid"
  | "unpaid"
  | "processing"
  | "failed"
  | "refunded"
  | "partial"
  | "succeeded";

type PaymentType = "credits" | "lesson" | "other";

interface PaymentListItem {
  id: string;
  type: PaymentType;
  direction: string; // "pay-in" | "payment"
  transaction_type?: string; // e.g. "user_created"
  status: {
    code: PaymentStatusCode | string;
    group: "completed" | "failed" | "pending" | "unknown" | string;
    label: string;
    icon?: string;
    color?: string;
  };
  amount: string; // formatted, e.g. "$45.00"
  converted_amount?: string | null;
  conversion_rate?: number | null;
  conversion_currency?: string | null;
  conversion_rate_date?: string | null;
  payment_due?: string | null;
  created_at?: string | null;
  processed_at?: string | null;
  archived?: boolean;
  archived_at?: string | null;
  description?: string | null;
  payer: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url: string | null;
    full_name: string;
  } | null;
  lesson_details: {
    lesson_id: string;
    workspace_id: string;
    topic: string;
    start_time_formatted?: string;
    start_time?: string;
    subject?: { id: string; name: string; icon?: string };
    amount?: string;
  } | null;
  credits_details: {
    id: string;
    credits: number;
    price_per_credit: string; // formatted
    total: string; // formatted
  } | null;
}

interface PaymentsListingResponse {
  data: PaymentListItem[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
  };
}

type SortOption =
  | "created_at"
  | "-created_at"
  | "processed_at"
  | "-processed_at"
  | "converted_amount"
  | "-converted_amount";

export default function PaymentsHistoryPage() {
  const { t } = useTranslation(["finances", "common"]);
  const { user } = useUserStore();
  const userTz = user?.timezone || "UTC";
  const navigate = useNavigate();

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<PaymentType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<string | "all">("all");
  const [sort, setSort] = useState<SortOption>("-created_at");
  const [displayCurrency, setDisplayCurrency] = useState<string>("USD");
  // Ranges (single calendar each)
  const [createdFrom, setCreatedFrom] = useState<Date | undefined>(undefined);
  const [createdTo, setCreatedTo] = useState<Date | undefined>(undefined);
  const [paidFrom, setPaidFrom] = useState<Date | undefined>(undefined);
  const [paidTo, setPaidTo] = useState<Date | undefined>(undefined);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);

  // Data
  const [items, setItems] = useState<PaymentListItem[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [perPage, setPerPage] = useState<number>(15);
  const [editOpen, setEditOpen] = useState(false);
  const [editPaymentId, setEditPaymentId] = useState<string | null>(null);
  const [editCurrent, setEditCurrent] = useState<
    | {
        amount?: string | null;
        currency?: string | null;
        payment_due?: string | null;
        description?: string | null;
        isCompleted?: boolean;
      }
    | undefined
  >();
  const [createOpen, setCreateOpen] = useState(false);

  const statusOptions = useMemo(
    () => [
      { id: "all", label: t("common:all", "All") },
      { id: "created", label: t("finances:paymentsHistory.statusLabels.created", "Created") },
      {
        id: "requires_confirmation",
        label: t(
          "finances:paymentsHistory.statusLabels.requires_confirmation",
          "Requires confirmation",
        ),
      },
      {
        id: "waiting_for_confirmation",
        label: t(
          "finances:paymentsHistory.statusLabels.waiting_for_confirmation",
          "Waiting for confirmation",
        ),
      },
      { id: "paid", label: t("finances:checkout.details.paid", "Paid") },
      {
        id: "payment_rejected",
        label: t("finances:paymentsHistory.statusLabels.payment_rejected", "Payment rejected"),
      },
      {
        id: "out_of_charge",
        label: t("finances:paymentsHistory.statusLabels.out_of_charge", "Out of charge"),
      },
    ],
    [t],
  );

  const typeOptions = useMemo(
    () => [
      { id: "all", label: t("common:all", "All") },
      { id: "credits", label: t("finances:paymentsHistory.credits", "Credits") },
      { id: "lesson", label: t("finances:paymentsHistory.lesson", "Lesson") },
    ],
    [t],
  );

  // Status options are used only for filtering; row rendering reads color/label/icon from item.status.

  const fetchPayments = useDebouncedCallback(async (nextPage = 1) => {
    setIsLoading(true);
    try {
      const params: Record<string, string | number | boolean> = { page: nextPage };

      if (searchQuery.trim()) params.paid_item_title = searchQuery.trim();
      if (typeFilter !== "all") params.type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (sort) params.sort = sort;
      if (displayCurrency) params.display_currency = displayCurrency;
      if (createdFrom) params.created_from = formatInTimeZone(createdFrom, userTz, "yyyy-MM-dd");
      if (createdTo) params.created_to = formatInTimeZone(createdTo, userTz, "yyyy-MM-dd");
      if (paidFrom) params.paid_from = formatInTimeZone(paidFrom, userTz, "yyyy-MM-dd");
      if (paidTo) params.paid_to = formatInTimeZone(paidTo, userTz, "yyyy-MM-dd");
      if (perPage) params.per_page = perPage;
      if (includeArchived) params.include_deleted = 1;

      const { data } = await axiosApiInstance.get<PaymentsListingResponse>(
        API_ENDPOINTS.TUTOR.FINANCES.PAYMENTS_LISTING,
        { params },
      );

      setItems(data.data);
      setPage(data.meta?.current_page ?? 1);
      setLastPage(data.meta?.last_page ?? 1);
    } catch (error) {
      console.error("Failed to fetch payments listing", error);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  const resetFilters = () => {
    setSearchQuery("");
    setTypeFilter("all");
    setStatusFilter("all");
    setSort("-processed_at");
    setDisplayCurrency("USD");
    setCreatedFrom(undefined);
    setCreatedTo(undefined);
    setPaidFrom(undefined);
    setPaidTo(undefined);
    setPerPage(15);
    setIncludeArchived(false);
    fetchPayments(1);
  };

  const onChangePage = (p: number) => {
    setPage(p);
    fetchPayments(p);
  };

  useEffect(() => {
    // Fetch immediately on any filter change (no debounce as requested)
    fetchPayments(1);
  }, [
    searchQuery,
    typeFilter,
    statusFilter,
    sort,
    displayCurrency,
    createdFrom,
    createdTo,
    paidFrom,
    paidTo,
    perPage,
    userTz,
    includeArchived,
  ]);

  return (
    <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
      <ScrollArea className="h-[calc(100dvh_-_4rem)]">
        <div className="mx-auto max-w-7xl p-2 pt-0! sm:p-3 lg:p-6">
          {/* Hero Header with integrated filters */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="bg-side-accent/50 text-accent-foreground relative mb-6 rounded-2xl border-none p-4 shadow-2xl md:mb-8 md:p-8"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden rounded-2xl">
              <div className="absolute -top-10 -left-10 h-24 w-24 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 opacity-5 md:-top-20 md:-left-20 md:h-40 md:w-40" />
              <div className="absolute -right-8 -bottom-8 h-20 w-20 rounded-full bg-gradient-to-tr from-green-400 to-teal-500 opacity-5 md:-right-16 md:-bottom-16 md:h-32 md:w-32" />
            </div>

            <div className="relative">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex items-center gap-4 md:gap-6">
                  <motion.div
                    animate={{ scale: 1 }}
                    className="bg-muted/20 flex items-center justify-center rounded-2xl p-2 backdrop-blur-sm md:p-4"
                    initial={{ scale: 0.9 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  >
                    <Icon
                      className="text-accent-foreground"
                      height="1.5rem"
                      icon="tabler:receipt"
                      width="1.5rem"
                    />
                  </motion.div>
                  <div>
                    <h1 className="text-accent-foreground text-2xl leading-tight font-bold md:text-3xl">
                      {t("finances:paymentsHistory.title", "Payments History")}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm md:mt-2 md:text-base">
                      {t(
                        "finances:paymentsHistory.subtitle",
                        "Browse tutor payments, filter by type or date and export invoices",
                      )}
                    </p>
                  </div>
                </div>

                {/* Right actions & search */}
                <div className="w-full md:max-w-md">
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                    <Button
                      className="w-full gap-2 sm:w-auto"
                      variant="foreground"
                      onClick={() => setCreateOpen(true)}
                    >
                      <Icon height="1rem" icon="mdi:credit-card-plus" width="1rem" />
                      {t("finances:createPayment.new", "New payment")}
                    </Button>
                  </div>
                  <div className="relative my-auto">
                    <Input
                      className="pr-16 md:pr-20"
                      placeholder={t(
                        "finances:paymentsHistory.searchPlaceholder",
                        "Search by title...",
                      )}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
                      {searchQuery && (
                        <Button
                          className="h-6 w-6 p-0"
                          size="sm"
                          type="button"
                          variant="ghost"
                          onClick={() => setSearchQuery("")}
                        >
                          <Icon height="1rem" icon="tabler:x" width="1rem" />
                        </Button>
                      )}
                      <div className="flex h-6 w-6 items-center justify-center">
                        <Icon height="1rem" icon="tabler:search" width="1rem" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick filters */}
              <div className="mt-4 grid grid-cols-1 gap-3 md:flex md:items-center md:gap-3">
                {/* Type */}
                <div className="flex items-center gap-2 md:flex-grow">
                  <div className="hidden text-xs font-medium md:block">
                    {t("finances:paymentsHistory.type", "Type")}:
                  </div>
                  <ToggleGroup
                    className="bg-muted/20 w-full rounded-md px-1 py-0.5 md:w-auto"
                    type="single"
                    value={typeFilter}
                    onValueChange={(v) => setTypeFilter((v || "all") as PaymentType | "all")}
                  >
                    {typeOptions.map((o) => (
                      <ToggleGroupItem
                        key={o.id}
                        className="flex-1 px-2 py-1 text-xs md:flex-none"
                        value={o.id}
                      >
                        {o.label}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>

                {/* Sort & Actions */}
                <div className="grid grid-cols-1 gap-2 sm:flex sm:items-center sm:gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="h-10 w-full text-xs sm:w-auto" variant="foreground">
                        {t("finances:paymentsHistory.sort", "Sort")}
                        <ChevronDown className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-foreground border-border/50 w-60 backdrop-blur-xl">
                      <DropdownMenuRadioGroup
                        value={sort}
                        onValueChange={(v) => setSort(v as SortOption)}
                      >
                        {[
                          {
                            id: "-processed_at",
                            label: t("finances:paymentsHistory.sortNewest", "Newest processed"),
                          },
                          {
                            id: "processed_at",
                            label: t("finances:paymentsHistory.sortOldest", "Oldest processed"),
                          },
                          {
                            id: "-created_at",
                            label: t(
                              "finances:paymentsHistory.sortCreatedNewest",
                              "Created newest",
                            ),
                          },
                          {
                            id: "created_at",
                            label: t(
                              "finances:paymentsHistory.sortCreatedOldest",
                              "Created oldest",
                            ),
                          },
                          {
                            id: "-converted_amount",
                            label: t(
                              "finances:paymentsHistory.sortAmountHigh",
                              "Amount high → low",
                            ),
                          },
                          {
                            id: "converted_amount",
                            label: t("finances:paymentsHistory.sortAmountLow", "Amount low → high"),
                          },
                        ].map((o) => (
                          <DropdownMenuRadioItem key={o.id} value={o.id}>
                            {o.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="flex gap-2">
                    <Button
                      className="h-10 flex-1 text-xs sm:flex-none"
                      variant="foreground"
                      onClick={() => setAdvancedOpen((v) => !v)}
                    >
                      <SlidersHorizontal className="mr-1 h-3.5 w-3.5" />
                      {advancedOpen
                        ? t("common:hideFilters", "Hide filters")
                        : t("common:filters", "Filters")}
                    </Button>

                    <Button
                      className="h-10 flex-1 text-xs sm:flex-none"
                      variant="outline"
                      onClick={resetFilters}
                    >
                      <Icon className="mr-1" height="1rem" icon="mdi:refresh" width="1rem" />
                      {t("common:reset", "Reset")}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Advanced filters */}
              <AnimatePresence initial={false}>
                {advancedOpen && (
                  <motion.div
                    key="extra-filters"
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Status */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="h-10 w-full justify-between text-sm"
                          variant="foreground"
                        >
                          <span className="truncate">
                            {t("finances:paymentsHistory.status", "Status")}:
                            <span className="ml-1 opacity-80">
                              {statusOptions.find((o) => o.id === statusFilter)?.label}
                            </span>
                          </span>
                          <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-foreground border-border/50 w-56 backdrop-blur-xl">
                        <DropdownMenuRadioGroup
                          value={statusFilter}
                          onValueChange={(v) => setStatusFilter(v)}
                        >
                          {statusOptions.map((o) => (
                            <DropdownMenuRadioItem key={o.id} value={o.id}>
                              {o.label}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Created range */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button className="h-10 w-full justify-start text-sm" variant="foreground">
                          <Calendar className="mr-2 h-4 w-4 flex-shrink-0" />
                          <span className="truncate">
                            {createdFrom && createdTo
                              ? `${format(createdFrom, "MMM d", { locale: getDateFnsLocale(i18n.language) })} - ${format(createdTo, "MMM d", { locale: getDateFnsLocale(i18n.language) })}`
                              : t("finances:paymentsHistory.createdRange", "Created range")}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="bg-foreground w-[calc(100vw-2rem)] max-w-sm p-2"
                      >
                        <UICalendar
                          initialFocus
                          locale={getDateFnsLocale(i18n.language)}
                          mode="range"
                          selected={{ from: createdFrom, to: createdTo } as any}
                          weekStartsOn={1}
                          onSelect={(range: any) => {
                            setCreatedFrom(range?.from);
                            setCreatedTo(range?.to);
                          }}
                        />
                        <div className="mt-2 flex items-center justify-between">
                          <Button
                            className="h-8"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setCreatedFrom(undefined);
                              setCreatedTo(undefined);
                            }}
                          >
                            {t("common:clear", "Clear")}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Paid range */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button className="h-10 w-full justify-start text-sm" variant="foreground">
                          <Calendar className="mr-2 h-4 w-4 flex-shrink-0" />
                          <span className="truncate">
                            {paidFrom && paidTo
                              ? `${format(paidFrom, "MMM d", { locale: getDateFnsLocale(i18n.language) })} - ${format(paidTo, "MMM d", { locale: getDateFnsLocale(i18n.language) })}`
                              : t("finances:paymentsHistory.paidRange", "Paid range")}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="bg-foreground w-[calc(100vw-2rem)] max-w-sm p-2"
                      >
                        <UICalendar
                          initialFocus
                          locale={getDateFnsLocale(i18n.language)}
                          mode="range"
                          selected={{ from: paidFrom, to: paidTo } as any}
                          weekStartsOn={1}
                          onSelect={(range: any) => {
                            setPaidFrom(range?.from);
                            setPaidTo(range?.to);
                          }}
                        />
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                          <Button
                            className="h-8"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPaidFrom(undefined);
                              setPaidTo(undefined);
                            }}
                          >
                            {t("common:clear", "Clear")}
                          </Button>
                          <Button
                            className="h-8"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const now = new Date();
                              const from = addDays(now, -7);

                              setPaidFrom(from);
                              setPaidTo(now);
                            }}
                          >
                            {t("finances:paymentsHistory.last7Days", "Last 7 days")}
                          </Button>
                          <Button
                            className="h-8"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const now = new Date();
                              const from = addDays(now, -30);

                              setPaidFrom(from);
                              setPaidTo(now);
                            }}
                          >
                            {t("finances:paymentsHistory.last30Days", "Last 30 days")}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Per page */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="h-10 w-full justify-between text-sm"
                          variant="foreground"
                        >
                          <span className="truncate">
                            {t("finances:paymentsHistory.perPage", "Per page")}:
                            <span className="ml-1 opacity-80">{perPage}</span>
                          </span>
                          <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-foreground border-border/50 w-32 backdrop-blur-xl">
                        <DropdownMenuRadioGroup
                          value={String(perPage)}
                          onValueChange={(v) => setPerPage(Number(v))}
                        >
                          {[15, 25, 50, 100].map((n) => (
                            <DropdownMenuRadioItem key={n} value={String(n)}>
                              {n}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Include archived */}
                    <Button
                      className={cn(
                        "h-10 w-full justify-between text-sm",
                        includeArchived && "bg-accent-foreground/50",
                      )}
                      variant="foreground"
                      onClick={() => setIncludeArchived((v) => !v)}
                    >
                      <span className="inline-flex items-center gap-2 truncate">
                        <Icon height="1rem" icon="mdi:archive" width="1rem" />
                        {t("finances:paymentsHistory.includeArchived", "Include archived")}
                      </span>
                    </Button>

                    {/* Display currency */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="h-10 w-full justify-between text-sm"
                          variant="foreground"
                        >
                          <span className="truncate">
                            {t("finances:paymentsHistory.displayCurrency", "Display currency")}:
                            <span className="ml-1 opacity-80">{displayCurrency}</span>
                          </span>
                          <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-foreground border-border/50 w-40 backdrop-blur-xl">
                        <DropdownMenuRadioGroup
                          value={displayCurrency}
                          onValueChange={(v) => setDisplayCurrency(v)}
                        >
                          {["USD", "EUR", "GBP", "PLN"].map((c) => (
                            <DropdownMenuRadioItem key={c} value={c}>
                              {c}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Content */}
          {isLoading ? (
            <Card className="bg-foreground/50 flex h-96 items-center justify-center backdrop-blur-sm">
              <GenericLoader />
            </Card>
          ) : items.length === 0 ? (
            <Card className="bg-foreground/50 flex h-64 flex-col items-center justify-center backdrop-blur-sm">
              <Icon
                className="text-muted-foreground mb-3"
                height="2rem"
                icon="mdi:calendar-blank"
                width="2rem"
              />
              <p className="text-accent-foreground text-base font-semibold">
                {t("finances:paymentsHistory.noResults", "No payments found")}
              </p>
              <p className="text-muted-foreground text-sm">
                {t("finances:paymentsHistory.noResultsHint", "Try adjusting filters or date range")}
              </p>
            </Card>
          ) : (
            <motion.div
              className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
              layout={"position"}
            >
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  animate={{ opacity: 1, y: 0 }}
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card
                    aria-disabled={item.transaction_type !== "user_created"}
                    className={cn(
                      "group bg-side-accent/50 relative h-full p-4 backdrop-blur-xl transition-all duration-300 hover:shadow-2xl",
                      item.transaction_type === "user_created"
                        ? "hover:bg-muted/20 cursor-pointer hover:scale-[1.02]"
                        : "cursor-default opacity-95",
                      item.archived && "opacity-60",
                    )}
                    role={item.transaction_type === "user_created" ? "button" : undefined}
                    tabIndex={item.transaction_type === "user_created" ? 0 : -1}
                    onClick={() => {
                      if (item.transaction_type === "user_created") {
                        navigate(PATHS.TUTOR.FINANCES.PAYMENT_DETAILS.replace(":id", item.id));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (item.transaction_type !== "user_created") return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(PATHS.TUTOR.FINANCES.PAYMENT_DETAILS.replace(":id", item.id));
                      }
                    }}
                  >
                    <div
                      className={
                        "absolute inset-0 top-0 left-0 h-full w-full overflow-hidden rounded-lg"
                      }
                    >
                      <div
                        className={cn(
                          "pointer-events-none absolute -top-6 -right-6 h-20 w-20 rounded-full opacity-20 blur-2xl",
                          item.archived ? "bg-muted" : "bg-gradient-to-br",
                        )}
                        style={
                          !item.archived ? { backgroundColor: (item.status as any)?.color } : {}
                        }
                      />
                    </div>

                    {/* Amount Display - Top Right */}
                    <div className="absolute top-3 right-3 z-50 text-right">
                      <div className="text-accent-foreground text-lg font-bold max-sm:text-sm">
                        {item.conversion_rate && item.conversion_rate !== 1 ? (
                          <TooltipProvider>
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <span>{item.converted_amount || item.amount}</span>
                              </TooltipTrigger>
                              <TooltipContent side="left">
                                <span>
                                  {t(
                                    "finances:paymentsHistory.conversionTooltip",
                                    "Converted from {{originalAmount}} at a rate of {{rate}} on {{date}}",
                                    {
                                      originalAmount: item.amount,
                                      rate: String(item.conversion_rate),
                                      date:
                                        format(item.conversion_rate_date ?? new Date(), "PPP", {
                                          locale: currentLanguageLocale(),
                                        }) || "",
                                    },
                                  )}
                                </span>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          item.amount
                        )}
                      </div>
                      {item.conversion_rate && item.conversion_rate !== 1 && (
                        <div className="text-muted-foreground text-xs font-normal">
                          {item.amount}
                        </div>
                      )}
                      {/* Archived Badge - Below Amount */}
                      {item.archived && (
                        <div className="bg-background/70 text-muted-foreground mt-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] backdrop-blur-xl">
                          <Icon height="0.75rem" icon="mdi:archive" width="0.75rem" />
                          {t("finances:paymentsHistory.archived", "Archived")}
                        </div>
                      )}
                    </div>

                    <div className="relative flex h-full flex-col">
                      {/* Header with Status and Icon */}
                      <div className="mb-4 flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="bg-muted/30 group-hover:bg-muted/40 flex items-center justify-center rounded-lg p-2 transition-colors">
                            <Icon
                              height="1.5rem"
                              icon={item.status.icon ?? "mdi:currency-usd"}
                              style={{
                                color: (item.status as any)?.color,
                              }}
                              width="1.5rem"
                            />
                          </div>
                          <div className="flex flex-col">
                            <span
                              className="inline-flex items-center gap-1.5 text-xs font-medium"
                              style={{ color: (item.status as any)?.color }}
                            >
                              <span
                                className="inline-block h-2 w-2 rounded-full"
                                style={{ backgroundColor: (item.status as any)?.color }}
                              />
                              {(item.status as any)?.label}
                            </span>
                            <span className="text-muted-foreground mt-0.5 text-[10px] tracking-wide uppercase">
                              {item.type === "lesson"
                                ? t("finances:paymentsHistory.lesson", "Lesson")
                                : item.type === "credits"
                                  ? t("finances:paymentsHistory.credits", "Credits")
                                  : t("finances:paymentsHistory.other", "Other")}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Title */}
                      <div className="mb-4">
                        <h3 className="text-accent-foreground group-hover:text-accent-foreground/90 line-clamp-2 text-base leading-tight font-semibold">
                          {item.type === "lesson"
                            ? item.lesson_details?.topic ||
                              t("workspaces:untitledLesson", "Untitled Lesson")
                            : item.type === "credits"
                              ? t("finances:paymentsHistory.creditsPurchase", "Credits purchase")
                              : item.description ||
                                t("finances:paymentsHistory.otherPayment", "Other payment")}
                        </h3>
                      </div>

                      {/* Payer Info */}
                      {item.payer && (
                        <div className="mb-4 flex items-center gap-2">
                          <Avatar className="size-8 rounded-full">
                            <AvatarImage
                              alt={item.payer.full_name}
                              src={item.payer.avatar_url || undefined}
                            />
                            <AvatarFallback className="text-xs">
                              {item.payer.first_name?.[0]}
                              {item.payer.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="text-accent-foreground truncate text-sm font-medium">
                              {item.payer.full_name}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Details */}
                      <div className="text-muted-foreground mb-4 space-y-2 text-xs">
                        {/* Lesson Details */}
                        {item.type === "lesson" && item.lesson_details && (
                          <div className="space-y-1">
                            {item.lesson_details.subject && (
                              <div className="flex items-center gap-1.5">
                                <Icon
                                  height="0.875rem"
                                  icon={item.lesson_details.subject.icon || "mdi:book-open"}
                                  width="0.875rem"
                                />
                                <span>{item.lesson_details.subject.name}</span>
                              </div>
                            )}
                            {item.lesson_details.start_time_formatted && (
                              <div className="flex items-center gap-1.5">
                                <Icon height="0.875rem" icon="mdi:calendar" width="0.875rem" />
                                <span>
                                  {format(new Date(item.lesson_details.start_time ?? ""), "PPPp", {
                                    locale: currentLanguageLocale(),
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Credits Details */}
                        {/*{item.type === "credits" && item.credits_details && (*/}
                        {/*  <div className="space-y-1">*/}
                        {/*    <div className="flex items-center gap-1.5">*/}
                        {/*      <Icon height="0.875rem" icon="tabler:coins" width="0.875rem" />*/}
                        {/*      <span>*/}
                        {/*        {item.credits_details.credits}{" "}*/}
                        {/*        {t("finances:buyCredits.credits", "credits")}*/}
                        {/*      </span>*/}
                        {/*    </div>*/}
                        {/*    <div className="flex items-center gap-1.5">*/}
                        {/*      <Icon height="0.875rem" icon="mdi:currency-usd" width="0.875rem" />*/}
                        {/*      <span>*/}
                        {/*        {item.credits_details.price_per_credit}{" "}*/}
                        {/*        {t("finances:buyCredits.perCredit", "per credit")}*/}
                        {/*      </span>*/}
                        {/*    </div>*/}
                        {/*  </div>*/}
                        {/*)}*/}

                        {/* Payment Due Date */}
                        {item.payment_due && (
                          <div className="flex items-center gap-1.5">
                            <Icon height="0.875rem" icon="mdi:calendar-clock" width="0.875rem" />
                            <span>
                              {t("finances:paymentsHistory.paymentDue", "Payment due")}:{" "}
                              {format(new Date(item.payment_due), "PPP", {
                                locale: currentLanguageLocale(),
                              })}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons - Always at bottom */}
                      <div className="mt-auto">
                        {item.transaction_type === "user_created" && (
                          <div className="flex items-center gap-2">
                            <TooltipProvider>
                              <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                  <Button
                                    className="h-9 flex-1 text-xs transition-transform hover:scale-[1.02]"
                                    variant="foreground"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        PATHS.TUTOR.FINANCES.PAYMENT_DETAILS.replace(
                                          ":id",
                                          item.id,
                                        ),
                                      );
                                    }}
                                  >
                                    <Icon height="0.875rem" icon="mdi:eye" width="0.875rem" />
                                    {t("common:viewDetails", "View Details")}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  {t("common:viewDetails", "View Details")}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                              <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                  <Button
                                    className="bg-muted/20 hover:bg-muted/30 h-9 w-9 p-0 transition-colors"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditPaymentId(item.id);
                                      setEditCurrent({
                                        amount: item.amount,
                                        currency: undefined,
                                        payment_due: item.payment_due || undefined,
                                        description: item.description || undefined,
                                        isCompleted: (item.status as any)?.group === "completed",
                                      });
                                      setEditOpen(true);
                                    }}
                                  >
                                    <Icon height="0.875rem" icon="mdi:pencil" width="0.875rem" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  {t("finances:paymentsEdit.tooltip", "Edit payment")}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Pagination */}
          {lastPage > 1 && (
            <div className="flex items-center justify-center gap-4 py-8">
              <div className="flex items-center gap-2">
                {/* Previous Page */}
                <Button
                  className="h-10 w-10 p-0"
                  disabled={page <= 1}
                  variant="outline"
                  onClick={() => onChangePage(page - 1)}
                >
                  <Icon height="1rem" icon="mdi:chevron-left" width="1rem" />
                </Button>

                {/* Page Numbers */}
                {(() => {
                  const currentPage = page;
                  const lastPageNum = lastPage;
                  const pages = [];

                  // Show first page if we're not close to it
                  if (currentPage > 3) {
                    pages.push(
                      <Button
                        key={1}
                        className="h-10 w-10 p-0"
                        variant="outline"
                        onClick={() => onChangePage(1)}
                      >
                        1
                      </Button>,
                    );
                  }

                  // Show pages around current page
                  const start = Math.max(1, currentPage - 2);
                  const end = Math.min(lastPageNum, currentPage + 2);

                  for (let i = start; i <= end; i++) {
                    const isCurrentPage = i === currentPage;

                    pages.push(
                      <Button
                        key={i}
                        className={cn(
                          "h-10 w-10 p-0",
                          isCurrentPage &&
                            "bg-accent-foreground text-primary hover:bg-accent-foreground/75",
                        )}
                        variant={isCurrentPage ? "default" : "outline"}
                        onClick={() => onChangePage(i)}
                      >
                        {i}
                      </Button>,
                    );
                  }

                  // Show last page if we're not close to it
                  if (currentPage < lastPageNum - 2) {
                    pages.push(
                      <Button
                        key={lastPageNum}
                        className="h-10 w-10 p-0"
                        variant="outline"
                        onClick={() => onChangePage(lastPageNum)}
                      >
                        {lastPageNum}
                      </Button>,
                    );
                  }

                  return pages;
                })()}

                {/* Next Page */}
                <Button
                  className="h-10 w-10 p-0"
                  disabled={page >= lastPage}
                  variant="outline"
                  onClick={() => onChangePage(page + 1)}
                >
                  <Icon height="1rem" icon="mdi:chevron-right" width="1rem" />
                </Button>
              </div>
            </div>
          )}
          <EditPaymentDialog
            current={editCurrent}
            open={editOpen}
            paymentId={editPaymentId}
            onOpenChange={setEditOpen}
            onUpdated={async () => fetchPayments(page)}
          />
          <CreatePaymentDialog
            open={createOpen}
            onCreated={async () => fetchPayments(page)}
            onOpenChange={(o) => setCreateOpen(o)}
          />
        </div>
      </ScrollArea>
    </div>
  );
}
